import { AbstractControl, FormControl, FormGroup, FormArray, ValidatorFn } from '@angular/forms';
import { atLeastOneGroupValidator } from '../authentication-method-policy-utlity';
import { AuthenticationMethodsCertificateBasedSchema } from './model';

// Trust affinity is fixed per cert field — not user-configurable
export const CERT_FIELD_TRUST_AFFINITY: Record<string, 'low' | 'high'> = {
    RFC822Name: 'low',
    PrincipalName: 'low',
    IssuerAndSubject: 'low',
    Subject: 'low',
    IssuerAndSerialNumber: 'high',
    SubjectKeyIdentifier: 'high',
    SHA1PublicKey: 'high',
};

export const CERT_FIELD_OPTIONS = [
    'RFC822Name',
    'PrincipalName',
    'SubjectKeyIdentifier',
    'SHA1PublicKey',
    'IssuerAndSerialNumber',
    'IssuerAndSubject',
    'Subject',
];

export const USER_PROPERTY_MAP: Record<string, string[]> = {
    RFC822Name: ['onPremisesUserPrincipalName', 'userPrincipalName', 'certificateUserIds'],
    PrincipalName: ['onPremisesUserPrincipalName', 'userPrincipalName', 'certificateUserIds'],
    SubjectKeyIdentifier: ['certificateUserIds'],
    SHA1PublicKey: ['certificateUserIds'],
    IssuerAndSerialNumber: ['certificateUserIds'],
    IssuerAndSubject: ['certificateUserIds'],
    Subject: ['certificateUserIds'],
};

const HIGH_AFFINITY_FIELDS = ['IssuerAndSerialNumber', 'SubjectKeyIdentifier', 'SHA1PublicKey'];
const LOW_AFFINITY_FIELDS = ['RFC822Name', 'PrincipalName', 'IssuerAndSubject', 'Subject'];

export function affinityBindingValidator(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
        const state = formGroup.get('state')?.value;

        // Only validate affinity when state is enabled
        if (state !== 'enabled') {
            return null;
        }

        const authConfig = formGroup.get('authenticationModeConfiguration');
        const requiredAffinity = authConfig?.get('x509CertificateDefaultRequiredAffinityLevel')?.value;
        const bindings = formGroup.get('certificateUserBindings') as FormArray;

        if (requiredAffinity === 'high') {
            const hasHighAffinityBinding = bindings?.controls?.some((binding: AbstractControl) => {
                const certField = binding.get('x509CertificateField')?.value;
                return HIGH_AFFINITY_FIELDS.includes(certField);
            });

            if (!hasHighAffinityBinding) {
                return { missingHighAffinityBinding: true };
            }
        }

        if (requiredAffinity === 'low') {
            // const hasLowAffinityBinding = bindings?.controls?.some((binding: AbstractControl) => {
            //     const certField = binding.get('x509CertificateField')?.value;
            //     return LOW_AFFINITY_FIELDS.includes(certField);
            // });

            if (bindings?.controls?.length <= 0) {
                return { missingLowAffinityBinding: true };
            }
        }

        return null;
    };
}

export function initAuthenticationMethodsCertificateBasedForm(
    schema: AuthenticationMethodsCertificateBasedSchema,
): FormGroup {
    const includedGroups = schema.properties?.includeTargets?.items?.oneOf;
    const includedGroupsFormArray = new FormArray([]);

    if (includedGroups?.length > 0) {
        includedGroups.forEach((group) => {
            const groupControl = new FormGroup({
                id: new FormControl(group.properties.id.const),
            });
            includedGroupsFormArray.push(groupControl);
        });
    } else {
        // Default to "all users" if no groups are specified
        const defaultGroupControl = new FormGroup({
            id: new FormControl('all_users'),
        });
        includedGroupsFormArray.push(defaultGroupControl);
    }

    const formGroup = new FormGroup({
        state: new FormControl(schema.properties.state.const || 'enabled'),

        includeTargets: new FormGroup(
            {
                includedGroups: includedGroupsFormArray,
                groupInclusion: new FormControl(
                    schema.properties?.includeTargets?.items?.oneOf?.some(
                        (res) => res?.properties?.id?.const !== 'all_users',
                    ) ?? false,
                ),
            },
            [atLeastOneGroupValidator()],
        ),

        excludeTargets: new FormControl(
            schema.properties?.excludeTargets?.items?.oneOf?.map((res) => res.properties.id.const) || [],
        ),

        authenticationModeConfiguration: new FormGroup({
            x509CertificateAuthenticationDefaultMode: new FormControl(
                schema.properties?.authenticationModeConfiguration?.properties?.x509CertificateAuthenticationDefaultMode
                    ?.const ?? 'x509CertificateSingleFactor',
            ),
            x509CertificateDefaultRequiredAffinityLevel: new FormControl(
                schema.properties?.authenticationModeConfiguration?.properties
                    ?.x509CertificateDefaultRequiredAffinityLevel?.const ?? 'low',
            ),
        }),

        certificateUserBindings: new FormArray(
            (() => {
                const rawItems = schema.properties?.certificateUserBindings?.items;
                const items = Array.isArray(rawItems) ? rawItems : rawItems ? [rawItems] : [];
                return items.map((item: any, index: number) => {
                    const certField = item.properties.x509CertificateField.const ?? 'PrincipalName';
                    return new FormGroup({
                        x509CertificateField: new FormControl(certField),
                        userProperty: new FormControl(item.properties.userProperty.const ?? 'userPrincipalName'),
                        priority: new FormControl(item.properties.priority.const ?? index + 1),
                        // Auto-derive trust affinity from cert field
                        trustAffinityLevel: new FormControl(CERT_FIELD_TRUST_AFFINITY[certField] ?? 'low'),
                    });
                });
            })(),
        ),

        crlValidationConfiguration: new FormGroup({
            state: new FormControl(
                schema.properties?.crlValidationConfiguration?.properties?.state?.const ?? 'enabled',
            ),
        }),

        issuerHintsConfiguration: new FormGroup({
            state: new FormControl(schema.properties?.issuerHintsConfiguration?.properties?.state?.const ?? 'enabled'),
        }),
    });

    // Re-validate includeTargets when includedGroups changes
    const includeTargetsGroup = formGroup.get('includeTargets') as FormGroup;
    const includedGroupsArray = includeTargetsGroup?.get('includedGroups') as FormArray;
    if (includedGroupsArray) {
        includedGroupsArray.valueChanges.subscribe(() => {
            includeTargetsGroup?.updateValueAndValidity({ emitEvent: false });
        });
    }

    // Re-validate includeTargets when state changes
    formGroup.get('state')?.valueChanges.subscribe(() => {
        includeTargetsGroup?.updateValueAndValidity({ emitEvent: false });
    });

    // Set affinity binding validator on the root form group
    formGroup.setValidators(affinityBindingValidator());

    // Re-run affinity validator when bindings or affinity level change
    formGroup.get('certificateUserBindings')?.valueChanges.subscribe(() => {
        formGroup.updateValueAndValidity({ emitEvent: false });
    });
    formGroup
        .get('authenticationModeConfiguration.x509CertificateDefaultRequiredAffinityLevel')
        ?.valueChanges.subscribe(() => {
            formGroup.updateValueAndValidity({ emitEvent: false });
        });

    return formGroup;
}

export function initAuthenticationMethodsCertificateBasedTemplateForm(
    schema: AuthenticationMethodsCertificateBasedSchema,
    override: string[],
): FormGroup {
    const formGroup = initAuthenticationMethodsCertificateBasedForm(schema);

    formGroup.addControl('excludeTargetsOverride', new FormControl(override?.includes('excludeTargets') || false));
    formGroup.addControl('includeTargetsOverride', new FormControl(override?.includes('includeTargets') || false));

    return formGroup;
}
